import { useEffect, useLayoutEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import {
    isRigidRegistration,
    RigidRegistration,
} from '~/domain/patient/rigidRegistration';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { useReferenceRow } from '~/hooks/viewer/useReferenceRow';
import { replanningCompareViewState } from '~/state/atoms/adaptiveReplanningAtoms';
import { useReviewDatasets } from './useReviewDatasets';
import { useReviewTransforms } from './useReviewTransforms';
import { notifyReferenceRowRefused } from './notifyReferenceRowRefused';

// The review comparison: the source scan in a row below the target, with its own structures and the prior dose, read-only.
export function useReferenceComparison(): void {
    const patient = useSelectedPatient();
    const { sourceSet, priorDose, propagatedSet } = useReviewDatasets();
    const { options } = useReviewTransforms();
    const active = useRecoilValue(replanningCompareViewState);
    const referenceRow = useReferenceRow();

    const planningImage = sourceSet?.image ?? undefined;
    const targetImage = propagatedSet?.image ?? undefined;
    // Navigation follows the run's rigid whichever transform the target is shown through.
    const runRigid = options.find((option) => option.kind === 'rigid');
    const preferred =
        runRigid && isRigidRegistration(runRigid.registration)
            ? runRigid.registration
            : undefined;
    const relates = (registration: RigidRegistration | undefined) =>
        !!registration &&
        !!planningImage &&
        !!targetImage &&
        registration.getTransformFromTo(
            targetImage.frameOfReferenceUid,
            planningImage.frameOfReferenceUid
        ) !== undefined;
    const relating = relates(preferred)
        ? preferred
        : planningImage && targetImage
          ? patient?.findRegistrationsBetween(planningImage, targetImage).at(0)
          : undefined;

    const held = useRef(referenceRow);
    useLayoutEffect(() => {
        held.current = referenceRow;
    });

    useEffect(() => {
        if (!active || !planningImage || !sourceSet || !relating) {
            return;
        }
        // The engine draws a dose on its own set, so a prior dose of another set is left out.
        const dose =
            priorDose?.structureSet === sourceSet ? priorDose : undefined;
        if (
            !held.current.show(planningImage, relating, {
                structureSet: sourceSet,
                dose,
            })
        ) {
            notifyReferenceRowRefused();
            return;
        }
        return () => held.current.end(planningImage);
    }, [active, planningImage, sourceSet, priorDose, relating]);
}
